<script setup>
import ReportFilterSelector from './FilterSelector.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
import { useI18n } from 'vue-i18n';
import SummaryReportLink from './SummaryReportLink.vue';

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];

const getMetrics = id =>
  reportMetrics.value.find(metrics => metrics.id === Number(id)) || {};
const columnHelper = createColumnHelper();
const { t } = useI18n();

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

const columns = computed(() => {
  const baseColumns = [
    columnHelper.accessor('name', {
      header: t(`SUMMARY_REPORTS.${props.type.toUpperCase()}`),
      width: 300,
      cell: cellProps => h(SummaryReportLink, cellProps),
    }),
    columnHelper.accessor('conversationsCount', {
      header: t('SUMMARY_REPORTS.CONVERSATIONS'),
      width: 200,
      cell: defaulSpanRender,
    }),
    columnHelper.accessor('avgFirstResponseTime', {
      header: t('SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME'),
      width: 200,
      cell: defaulSpanRender,
    }),
    columnHelper.accessor('avgResolutionTime', {
      header: t('SUMMARY_REPORTS.AVG_RESOLUTION_TIME'),
      width: 200,
      cell: defaulSpanRender,
    }),
    columnHelper.accessor('avgReplyTime', {
      header: t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
      width: 200,
      cell: defaulSpanRender,
    }),
  ];

  // Add agent-specific columns
  if (props.type === 'agent') {
    if (props.type === 'agent') {
      [
        'handlingHours',
        'repliesPerHandlingHour',
        'conversationsHandled',
        'failedSendsCount',
        'timingSampleCount',
        'timingCoverage',
        'unverifiedSessionsCount',
      ].forEach(key => {
        baseColumns.push(
          columnHelper.accessor(key, {
            header: t(`SUMMARY_REPORTS.${key}`),
            width: 180,
            cell: defaulSpanRender,
          })
        );
      });
    }

    baseColumns.push(
      columnHelper.accessor('avgTimePerEmail', {
        header: t('SUMMARY_REPORTS.AVG_TIME_PER_EMAIL'),
        width: 200,
        cell: defaulSpanRender,
      }),
      columnHelper.accessor('emailsSentCount', {
        header: t('SUMMARY_REPORTS.EMAILS_SENT'),
        width: 150,
        cell: defaulSpanRender,
      }),
      columnHelper.accessor('totalLoggedHours', {
        header: t('SUMMARY_REPORTS.TOTAL_LOGGED_HOURS'),
        width: 200,
        cell: defaulSpanRender,
      })
    );
  }

  baseColumns.push(
    columnHelper.accessor('resolutionsCount', {
      header: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
      width: 200,
      cell: defaulSpanRender,
    })
  );

  return baseColumns;
});

const renderAvgTime = value => (value == null ? '--' : formatTime(value));

const renderCount = value => (value == null ? '--' : value.toLocaleString());

const renderHours = value => {
  if (value == null) return '--';

  // Convert hours to seconds to use the same formatTime logic
  const timeInSeconds = value * 3600;
  return formatTime(timeInSeconds);
};

const reportTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const reportError = computed(
  () => store.getters['summaryReports/getUIFlags'][`${props.type}Error`]
);
const displayRows = computed(() =>
  props.type === 'agent' ? reportMetrics.value : rowItems.value
);
const tableData = computed(() =>
  displayRows.value.map(row => {
    const rowMetrics = getMetrics(row.id);
    const {
      conversationsCount,
      avgFirstResponseTime,
      avgResolutionTime,
      avgReplyTime,
      avgTimePerEmail,
      totalLoggedHours,
      emailsSentCount,
      resolvedConversationsCount,
    } = rowMetrics;
    return {
      id: row.id,
      archived: rowMetrics.archived || false,
      // we fallback on title, label for instance does not have a name property
      name: row.name ?? row.title,
      type: props.type,
      conversationsCount: renderCount(conversationsCount),
      avgFirstResponseTime: renderAvgTime(avgFirstResponseTime),
      avgReplyTime: renderAvgTime(avgReplyTime),
      avgResolutionTime: renderAvgTime(avgResolutionTime),
      avgTimePerEmail: renderAvgTime(avgTimePerEmail),
      emailsSentCount: renderCount(emailsSentCount),
      totalLoggedHours: renderHours(totalLoggedHours),
      resolutionsCount: renderCount(resolvedConversationsCount),
      handlingHours: renderHours(rowMetrics.handlingHours),
      repliesPerHandlingHour: renderCount(rowMetrics.repliesPerHandlingHour),
      conversationsHandled: renderCount(rowMetrics.conversationsHandled),
      failedSendsCount: renderCount(rowMetrics.failedSendsCount),
      timingSampleCount: renderCount(rowMetrics.timingSampleCount),
      timingCoverage:
        rowMetrics.timingCoverage == null
          ? '--'
          : `${rowMetrics.timingCoverage}%`,
      unverifiedSessionsCount: renderCount(rowMetrics.unverifiedSessionsCount),
    };
  })
);

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  store.dispatch(props.actionKey, {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  });
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateFileName({
      type: props.type,
      to: to.value,
      businessHours: businessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <ReportFilterSelector @filter-change="onFilterChange" />
  <p class="px-4 text-sm text-n-slate-11">
    {{ $t('SUMMARY_REPORTS.TIMEZONE', { timezone: reportTimezone }) }}
  </p>
  <div
    class="flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <p v-if="reportError" role="alert" class="p-4 text-n-ruby-11">
      {{ $t('SUMMARY_REPORTS.LOAD_ERROR') }}
    </p>
    <Table v-else :table="table" />
  </div>
</template>
